import { useEffect, useMemo } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FaEye, FaRegEdit, FaUserSlash } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { usePagination, useRowSelect, useTable, useFilters, useExpanded } from 'react-table';
import { toast } from 'react-toastify';
import DefaultColumnFilter from '../../../../Components/ReactTable/Filters/DefaultColumnFilter';
import ReactTableRender from '../../../../Components/ReactTable/ReactTableRender';
import { clearTable, handleExecutiveListChange } from '../../../../Redux/slices/table';
import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../../_helpers/_methods';
import Swal from 'sweetalert2';
import UserImage from '../../../../Components/UserImage';
import ToolTip from '../../../../Components/ToolTip';

function ExecutivesTable() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const pageNum = useSelector((state) => state.table.pageNum);
  const loading = useSelector((state) => state.table.loading);
  const totalCount = useSelector((state) => state.table.totalCount);
  const permissions = useSelector((state) => state.user.permissions);
  const results = useSelector((state) => state.table.results);
  const handleSuspend = (original) => {
    Swal.fire({
      title: intl.formatMessage({ id: 'are_you_sure' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes' }),
      cancelButtonText: intl.formatMessage({ id: 'cancel' }),
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (original.suspended) {
            await fetchWrapper.get(`/profile/executive/${original.id}/unsuspend/`);
            toast.success(<FormattedMessage id="unsuspended_successfully" />);
          } else {
            await fetchWrapper.get(`/profile/executive/${original.id}/suspend/`);
            toast.success(<FormattedMessage id="suspended_successfully" />);
          }
          dispatch(handleExecutiveListChange({ index: pageIndex, pageSize: pageSize }));
        } catch (error) {
          ErrorMessageHandler(error);
        }
      }
    });
  };
  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="userFullName" />,
        accessor: 'fullname',
        Cell: ({ row: { original }, cell: { value } }) => {
          return (
            <Link to={`profile/${original.id}`}>
              <span className="d-flex align-items-center ">
                <div className="me-1 position-relative">
                  <UserImage
                    avatar={original.image}
                    userName={value}
                    userId={String(original.id)}
                  />
                  <span className="avatar-profile-iscomplete-badge">
                    {/* {original.warnings && ( */}
                    <ToolTip
                      warnings={original.warnings}
                      isComplete={original.is_profile_updated}
                    />
                    {/* )} */}
                  </span>
                </div>

                <span className="d-block">
                  {value} <br />
                </span>
                {original.suspended && (
                  <Badge bg="danger">
                    <FormattedMessage id="suspended" />
                  </Badge>
                )}
              </span>
            </Link>
          );
        }
      },
      {
        Header: <FormattedMessage id="office_type" />,
        accessor: 'office_type'
      },
      {
        Header: <FormattedMessage id="province" />,
        accessor: 'province'
      },
      {
        Header: <FormattedMessage id="mobile_number" />,
        accessor: 'phone'
      },
      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'actions',
        disableFilters: true,
        Cell: ({ row: { original } }) => (
          <div className="d-flex">
            <div>
              <Link to={`profile/${original.id}`}>
                <Button variant="outline-info">
                  <FaEye />
                </Button>
              </Link>
            </div>
            {permissions['Can change executive'] && (
              <>
                <div className="ms-2">
                  <Link to={`edit/${original.id}`}>
                    <Button variant="outline-secondary ">
                      <FaRegEdit />
                    </Button>
                  </Link>
                </div>
                <div className="ms-2 ">
                  <Button
                    className="d-flex align-items-center justify-content-center "
                    style={{ minWidth: '150px' }}
                    variant="outline-danger "
                    onClick={() => handleSuspend(original)}>
                    <FaUserSlash className="me-2" />
                    {original.suspended ? (
                      <FormattedMessage id="unsuspend" />
                    ) : (
                      <FormattedMessage id="suspend" />
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        )
      }
    ],
    [permissions]
  );

  const data = useMemo(() => {
    if (results) {
      return results.map((result) => {
        return {
          id: result.id,
          fullname: result.fullname,
          office_type: result.office_type,
          province: result.province?.title,
          phone: result.phone,
          suspended: result.suspended,
          warnings: result.warnings,
          is_profile_updated: result.is_profile_updated
        };
      });
    }
  }, [results]);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn
      // manualFilters: true
    },
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    dispatch(handleExecutiveListChange({ index: pageIndex, pageSize: pageSize }));
  }, [pageIndex]);

  useEffect(() => {
    return () => {
      dispatch(clearTable());
    };
  }, []);
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount]
  );
  return (
    <div>
      <ReactTableRender
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        getTableBodyProps={getTableBodyProps}
        page={page}
        prepareRow={prepareRow}
        totalCount={totalCount}
        gotoPage={gotoPage}
        previousPage={previousPage}
        pageCount={pageCount}
        pagination={true}
        pageCountArray={pageCountArray}
        pageNum={pageNum}
        showGoto={true}
        nextPage={nextPage}
        pageIndex={pageIndex}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        loading={loading}
        visibleColumns={visibleColumns}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </div>
  );
}

export default ExecutivesTable;
